/**
 * The app's half of the REVIEW guarantee (ORDR-30, FR-FLW-001).
 *
 * THIS DOES NOT REIMPLEMENT `evaluateFlow` — it calls it. The framework owns
 * the decision of whether a resolved flow is renderable (UIFR-1616,
 * `packages/components/src/shell/flow.ts`), and duplicating that logic here
 * would give the platform two answers to one question and no way to tell which
 * one a store was running on a Friday.
 *
 * What this file adds is the APPLICATION's consequence of that decision: a flow
 * the client refuses must not advance to payment, and the refusal has to be
 * reportable, because a server fault nobody is paged about is a server fault
 * that stays broken. `evaluateFlow` answers "may I render this?"; this answers
 * "may I take this person's money?".
 */

import { evaluateFlow, type FlowFault, type ResolvedFlow } from "@pirouz-ca/ui-components";

/** What the app decided to do with a resolved flow. */
export type FlowGateDecision =
  | { readonly proceed: true }
  | { readonly proceed: false; readonly fault: FlowFault };

/** Notified whenever the client refuses a flow. Both faults are the server's. */
export type FlowFaultReporter = (fault: FlowFault, flow: ResolvedFlow) => void;

/**
 * Decide whether this flow may advance toward payment.
 *
 * Delegates the whole judgement to the framework and reports a refusal. There
 * is deliberately no `force`, no `allowMissingReview` and no override: the one
 * way this can be made to continue past a missing REVIEW is for the server to
 * stop sending flows without one.
 */
export function gateFlow(flow: ResolvedFlow, report?: FlowFaultReporter): FlowGateDecision {
  const evaluation = evaluateFlow(flow);

  if (evaluation.status === "refused") {
    report?.(evaluation.fault, flow);
    return { proceed: false, fault: evaluation.fault };
  }

  return { proceed: true };
}
